import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';

const brandPrinciples = [
  {
    num: '01',
    title: '不只看事件，也看結構',
    description: '透過制度、權力、利益與社會條件，理解事件背後的形成原因。',
    icon: 'ri-stack-line',
  },
  {
    num: '02',
    title: '不只讀歷史，也讀現代',
    description: '以歷史事件、人物與制度作為理解現代社會的參照。',
    icon: 'ri-history-line',
  },
  {
    num: '03',
    title: '不只談商業，也談判斷',
    description: '從商業模式、風險、制度與選擇，理解市場和決策背後的邏輯。',
    icon: 'ri-scales-3-line',
  },
  {
    num: '04',
    title: '不只提供知識，也建立視角',
    description: '協助讀者將零散資訊整理為可長期使用的理解框架。',
    icon: 'ri-compass-3-line',
  },
];

export default function About() {
  const { ref: ref1, inView: inView1 } = useInView();
  const { ref: ref2, inView: inView2 } = useInView();
  const { ref: ref3, inView: inView3 } = useInView();

  const anim = (inView: boolean) =>
    `transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`;

  return (
    <>
      <Navbar />
      <main className="pt-0">
        <section
          ref={ref1}
          className="relative w-full min-h-[100svh] flex flex-col justify-center items-center overflow-hidden"
        >
          <div className="absolute inset-0">
            <video
              src="https://public.readdy.ai/ai/video_res/019fd9f1-9d8e-7121-b377-4696ed20cdab.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/65 via-primary-950/35 to-primary-950/55"></div>

          <div className="relative z-10 w-full px-6 md:px-10 pt-20 md:pt-24 pb-8">
          <div className={`max-w-3xl mx-auto w-full text-center ${anim(inView1)}`}>
            <p className="text-xs font-label tracking-widest text-white/70 mb-3 uppercase text-shadow-section">About</p>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight mb-4 text-shadow-section">
              山頂視角 PeakView
            </h1>
            <p className="font-heading text-lg md:text-2xl text-white/80 mb-8 leading-relaxed text-shadow-section">
              從宏觀脈絡理解現代世界
            </p>

            <div className="space-y-5">
              <p className="text-base md:text-lg text-white/75 leading-relaxed text-shadow-section">
                山頂視角 PeakView 為崧博出版經銷品牌，聚焦觀點分析與社會觀察。
              </p>
              <p className="text-base md:text-lg text-white/75 leading-relaxed text-shadow-section">
                關注商業、政治、歷史、社會與制度議題，從較高位置觀察事件與結構。
              </p>
              <p className="text-base md:text-lg text-white/75 leading-relaxed text-shadow-section">
                出版方向涵蓋商業理財、政治歷史、人文社科與社會觀察，協助讀者建立理解世界的視角。
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link
                to="/stores"
                className="px-6 py-3 bg-accent-500 text-primary-950 font-label font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap"
              >
                查看合作通路
              </Link>
              <a
                href="https://sonbook.net/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="px-6 py-3 bg-white/15 backdrop-blur-sm text-white font-label font-medium text-sm rounded-full hover:bg-white/25 border border-white/20 transition-all duration-300 whitespace-nowrap"
              >
                前往崧博出版
              </a>
            </div>
          </div>
          </div>
        </section>

        <section
          ref={ref2}
          className="min-h-[80svh] flex flex-col justify-center px-6 md:px-10 py-20 md:py-28 bg-secondary-50"
        >
          <div className={`max-w-6xl mx-auto w-full ${anim(inView2)}`}>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">
              品牌理念
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {brandPrinciples.map((principle) => (
                <div
                  key={principle.num}
                  className="group bg-background-50 rounded-lg p-6 md:p-7 hover:bg-background-200/70 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="font-heading text-4xl md:text-5xl font-bold text-foreground-200 group-hover:text-accent-200 transition-colors">
                      {principle.num}
                    </span>
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${principle.icon} text-sm text-accent-700`}></i>
                    </span>
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2">
                    {principle.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {principle.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          ref={ref3}
          className="min-h-[50svh] flex items-center px-6 md:px-10 py-16 md:py-20 bg-background-50"
        >
          <div className={`max-w-3xl mx-auto w-full text-center ${anim(inView3)}`}>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4">
              品牌經銷與合作
            </h2>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
              山頂視角 PeakView 是崧博出版經銷品牌。讀者可透過合作通路與書目資料庫查找書籍。
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/stores"
                className="px-5 py-3 bg-accent-500 text-primary-950 font-label font-medium text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap"
              >
                查看合作購書通路
              </Link>
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="px-5 py-3 bg-primary-500 text-white font-label font-medium text-sm rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
              >
                崧博出版書籍資料庫
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}